import { gql } from "@apollo/client";
import type { DependencySet } from "../../../../AppView/Dependencies/types";
import type { NameCollision, ScopeRef } from "./types";

export interface ScopeDependencies {
  direct: DependencySet;
  descendants_in_use: ScopeRef[];
  ancestors_have_dependencies: boolean;
}

const DEPENDENCY_SET_FRAGMENT = gql`
  fragment DependencySetFields on DependencySetType {
    total_count
    rules {
      id
      name
      kind
      enabled
    }
    automations {
      id
      name
      status
      state
    }
    ai_analysts {
      id
      name
      status
      state
      entity_id
    }
    widgets {
      id
      name
      dashboard_name
    }
    hidden_widgets_count
    hidden_dashboards_count
  }
`;

const SCOPE_DEPENDENCIES_FRAGMENT = gql`
  ${DEPENDENCY_SET_FRAGMENT}
  fragment ScopeDependenciesFields on ScopeDependenciesType {
    direct {
      ...DependencySetFields
    }
    descendants_in_use {
      id
      name
      node_type
    }
    ancestors_have_dependencies
  }
`;

export const FINDING_SCOPE_DEPENDENCIES = gql`
  ${SCOPE_DEPENDENCIES_FRAGMENT}
  query findingScopeDependencies($id: ID!) {
    finding_scope_dependencies(finding_scope_id: $id) {
      ...ScopeDependenciesFields
    }
  }
`;

export const SCOPE_GROUP_DEPENDENCIES = gql`
  ${SCOPE_DEPENDENCIES_FRAGMENT}
  query scopeGroupDependencies($id: ID!, $includeDescendants: Boolean) {
    scope_group_dependencies(
      scope_group_id: $id
      include_descendants: $includeDescendants
    ) {
      ...ScopeDependenciesFields
    }
  }
`;

export interface ScopeDependenciesQueryResponse {
  finding_scope_dependencies: ScopeDependencies | null;
}

export interface ScopeGroupDependenciesQueryResponse {
  scope_group_dependencies: ScopeDependencies | null;
}

export const DELETE_FINDING_SCOPE = gql`
  mutation deleteFindingScopeV3($id: ID!) {
    delete_finding_scope_v3(finding_scope_id: $id) {
      ok
    }
  }
`;

export interface DeleteScopeResponse {
  delete_finding_scope_v3: { ok: boolean };
}

export const DELETE_SCOPE_GROUP = gql`
  mutation deleteScopeGroupV3($id: ID!, $deleteDescendants: Boolean) {
    delete_scope_group_v3(
      scope_group_id: $id
      delete_descendants: $deleteDescendants
    ) {
      ok
      name_collisions {
        name
        node_type
      }
    }
  }
`;

export interface DeleteScopeGroupResponse {
  delete_scope_group_v3: { ok: boolean; name_collisions: NameCollision[] };
}

// Every guarded write resolves the same payload: refused on a name collision → ok:false
// with the colliding names; a dependency block raises instead.
export interface ScopeWritePayload {
  ok: boolean;
  name_collisions: NameCollision[];
}

export const RENAME_FINDING_SCOPE = gql`
  mutation renameFindingScopeV3($id: ID!, $name: String!) {
    rename_finding_scope_v3(finding_scope_id: $id, name: $name) {
      ok
      name_collisions {
        name
        node_type
      }
    }
  }
`;

export interface RenameScopeResponse {
  rename_finding_scope_v3: ScopeWritePayload;
}

// A null destination moves the scope to the top level.
export const MOVE_FINDING_SCOPE = gql`
  mutation moveFindingScopeV3($id: ID!, $scopeGroupId: ID) {
    move_finding_scope_v3(
      finding_scope_id: $id
      scope_group_id: $scopeGroupId
    ) {
      ok
      name_collisions {
        name
        node_type
      }
    }
  }
`;

export interface MoveScopeResponse {
  move_finding_scope_v3: ScopeWritePayload;
}

// A null parent moves the group (with its subtree) to the top level.
export const MOVE_SCOPE_GROUP = gql`
  mutation moveScopeGroupV3($id: ID!, $parentGroupId: ID) {
    move_scope_group_v3(scope_group_id: $id, parent_group_id: $parentGroupId) {
      ok
      name_collisions {
        name
        node_type
      }
    }
  }
`;

export interface MoveScopeGroupResponse {
  move_scope_group_v3: ScopeWritePayload;
}

// The member lists are the group's whole membership after the edit; members left
// out move to the top level.
export const EDIT_SCOPE_GROUP = gql`
  mutation editScopeGroupV3(
    $id: ID!
    $name: String!
    $childGroupIds: [ID!]!
    $childScopeIds: [ID!]!
  ) {
    edit_scope_group_v3(
      scope_group_id: $id
      name: $name
      child_group_ids: $childGroupIds
      child_scope_ids: $childScopeIds
    ) {
      ok
      name_collisions {
        name
        node_type
      }
    }
  }
`;

export interface EditScopeGroupResponse {
  edit_scope_group_v3: ScopeWritePayload;
}

// Shared blocker DTOs, re-exported so consumers of this layer never reach into AppView/Dependencies.
export type {
  DependencySet,
  DependencyRule,
  DependencyAutomation,
  DependencyAiAnalyst,
  DependencyWidget,
} from "../../../../AppView/Dependencies/types";
